import {
  FlowId,
  InfoPage,
  LegalPage,
  LocalizedText,
  PraxisConfig,
  PraxisConfigInput,
  PrivacyResponsible,
  PrivacyTemplate,
  ThemeColors,
} from "@shared/praxis-config";

export const textKeys = [
  "cancelAppointment",
  "additionalPaymentLegallyInsured",
  "additionalPaymentPrivateInsured",
] as const satisfies readonly (keyof PraxisConfig["texts"])[];

export type TextKey = (typeof textKeys)[number];

export const themeKeys = [
  "primary",
  "primaryHover",
  "textPrimary",
  "textPrimaryContrast",
  "bgMobileFrom",
  "bgDesktopFrom",
] as const satisfies readonly (keyof ThemeColors)[];

/** Empty strings stand for "not set" */
export type LocalizedTextValues = { de: string; en: string };

export type ConfigFormValues = {
  flow: FlowId;
  contactForm: boolean;
  /** Empty texts fall back to the default text */
  texts: Record<TextKey, LocalizedTextValues>;
  imprint: {
    type: "redirect" | "markdown";
    url: string;
    text: LocalizedTextValues;
  };
  privacy: {
    type: "template" | "redirect" | "markdown";
    url: string;
    text: LocalizedTextValues;
    responsible: PrivacyResponsible;
  };
  infoPages: {
    id: string;
    title: LocalizedTextValues;
    text: LocalizedTextValues;
  }[];
  useCustomTheme: boolean;
  theme: ThemeColors;
};

const emptyText = (): LocalizedTextValues => ({ de: "", en: "" });

const toTextValues = (text?: LocalizedText): LocalizedTextValues => ({
  de: text?.de ?? "",
  en: text?.en ?? "",
});

const toLocalizedText = ({ de, en }: LocalizedTextValues): LocalizedText =>
  en.trim() ? { de, en } : { de };

const isSet = (text: LocalizedTextValues) => text.de.trim() !== "";

const legalPageValues = (page?: LegalPage | PrivacyTemplate) => ({
  url: page?.type === "redirect" ? page.url : "",
  text: toTextValues(page?.type === "markdown" ? page.text : undefined),
});

export const toConfigFormValues = (
  input: PraxisConfigInput | null,
  defaults: PraxisConfig,
): ConfigFormValues => {
  const privacy = input?.legal.privacy;

  return {
    flow: input?.flow ?? defaults.flow,
    contactForm: input?.features?.contactForm ?? defaults.features.contactForm,
    texts: Object.fromEntries(
      textKeys.map((key) => [key, toTextValues(input?.texts?.[key])]),
    ) as ConfigFormValues["texts"],
    imprint: {
      type: input?.legal.imprint.type ?? "redirect",
      ...legalPageValues(input?.legal.imprint),
    },
    privacy: {
      type: privacy?.type ?? "template",
      ...legalPageValues(privacy),
      responsible:
        privacy?.type === "template"
          ? privacy.responsible
          : { name: "", address: "", email: "", phone: "" },
    },
    infoPages: (input?.infoPages ?? []).map((page) => ({
      id: page.id,
      title: toTextValues(page.title),
      text: toTextValues(page.text),
    })),
    useCustomTheme: !!input?.theme,
    theme: input?.theme ?? defaults.theme,
  };
};

const toLegalPage = (page: {
  type: "redirect" | "markdown";
  url: string;
  text: LocalizedTextValues;
}): LegalPage =>
  page.type === "redirect"
    ? { type: "redirect", url: page.url.trim() }
    : { type: "markdown", text: toLocalizedText(page.text) };

export const toConfigInput = (values: ConfigFormValues): PraxisConfigInput => {
  const texts = Object.fromEntries(
    textKeys
      .filter((key) => isSet(values.texts[key]))
      .map((key) => [key, toLocalizedText(values.texts[key])]),
  );
  const infoPages: InfoPage[] = values.infoPages.map((page) => ({
    id: page.id.trim(),
    title: toLocalizedText(page.title),
    text: toLocalizedText(page.text),
  }));
  const { privacy } = values;

  return {
    flow: values.flow,
    features: { contactForm: values.contactForm },
    ...(Object.keys(texts).length ? { texts } : {}),
    legal: {
      imprint: toLegalPage(values.imprint),
      privacy:
        privacy.type === "template"
          ? { type: "template", responsible: privacy.responsible }
          : toLegalPage({ ...privacy, type: privacy.type }),
    },
    ...(infoPages.length ? { infoPages } : {}),
    ...(values.useCustomTheme ? { theme: values.theme } : {}),
  };
};

export const emptyInfoPage = (): ConfigFormValues["infoPages"][number] => ({
  id: "",
  title: emptyText(),
  text: emptyText(),
});

export const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      // Strips the "data:<type>;base64," prefix
      resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
